if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch.querySelector('button').addEventListener('click', () => {
            this.setActiveMedia(mediaToSwitch.dataset.target, false);
            this.selectVariantForImage(mediaToSwitch.dataset.target);
          });
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }
      selectVariantForImage(mediaTarget) {
        const productInfo = this.closest('product-info');
        const picker = productInfo?.querySelector('variant-selects');
        const mapElement = this.querySelector('[data-image-variant-map]');

        if (!picker || !mapElement) return;

        let variants;
        try {
          variants = JSON.parse(mapElement.textContent);
        } catch (error) {
          console.error('Could not read image variant mapping', error);
          return;
        }

        const mediaId = String(mediaTarget).split('-').pop();
        const candidates = variants.filter(
          (variant) => String(variant.mediaId) === mediaId
        );

        if (!candidates.length) return;

        const groups = Array.from(
          picker.querySelectorAll('fieldset, .product-form__input--dropdown')
        );

        const currentOptions = groups.map((group) => {
          const select = group.querySelector('select');
          return select
            ? select.value
            : group.querySelector('input:checked')?.value;
        });

        const score = (variant) =>
          variant.options.reduce(
            (total, value, index) =>
              total + (value === currentOptions[index] ? 1 : 0),
            0
          );

        candidates.sort(
          (a, b) =>
            score(b) - score(a) ||
            Number(b.available) - Number(a.available)
        );

        const variant = candidates[0];
        if (variant.options.length !== groups.length) return;

        const controls = groups.map((group, index) => {
          const select = group.querySelector('select');

          if (select) {
            const option = Array.from(select.options).find(
              (item) => item.value === variant.options[index]
            );
            return option ? { input: select, option } : null;
          }

          const input = Array.from(
            group.querySelectorAll('input[type="radio"]')
          ).find((item) => item.value === variant.options[index]);

          return input ? { input } : null;
        });

        if (controls.some((control) => !control)) return;

        let changedInput = null;

        controls.forEach(({ input, option }) => {
          if (option) {
            if (input.value !== option.value) changedInput = input;
            input.value = option.value;

            Array.from(input.options).forEach((item) => {
              item.toggleAttribute('selected', item === option);
            });
          } else {
            if (!input.checked) changedInput = input;
            input.checked = true;
          }

          picker.updateSelectionMetadata({ target: input });
        });

        if (changedInput) {
          changedInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
